import { ReactNode, useEffect, useRef, useState } from "react";

/**
 * SectionHeader — unified header for landing-page sections.
 *
 * Eyebrow, headline and subtitle fade-up with a soft stagger as the header
 * scrolls into view — the same editorial motion as before, now driven by a
 * tiny IntersectionObserver + CSS keyframes (see .sh-reveal in index.css)
 * instead of framer-motion, so the ~42 KB-gz motion chunk stays off the
 * homepage critical path.
 *
 * Progressive enhancement: the hidden ("armed") state is only applied when
 * IntersectionObserver exists, so prerender / no-JS / reduced-motion always
 * render the header fully visible — it can never be stuck blank.
 */
interface Props {
  eyebrow: string;
  title: ReactNode;
  subtitle?: ReactNode;
  align?: "left" | "center";
  className?: string;
  id?: string;
}

const SectionHeader = ({
  eyebrow,
  title,
  subtitle,
  align = "center",
  className,
  id,
}: Props) => {
  const isCenter = align === "center";
  const ref = useRef<HTMLDivElement | null>(null);
  // Only arm the entrance on clients that can observe scroll position. The
  // initializer runs before the first client paint (CSR), so the header
  // renders hidden from the start when armed — no visible flash-then-hide.
  const [armed] = useState(
    () => typeof window !== "undefined" && "IntersectionObserver" in window,
  );
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [armed]);

  // shown = always visible (no JS/IO); armed = hidden, waiting; in = play.
  const dataSh = !armed ? "shown" : inView ? "in" : "armed";

  return (
    <div
      ref={ref}
      data-sh={dataSh}
      className={[
        "sh-reveal",
        isCenter ? "text-center mx-auto" : "text-left",
        "max-w-3xl mb-12 sm:mb-16",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <p className="sh-eyebrow text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-mono mb-4">
        {eyebrow}
      </p>
      <h2 id={id} className="sh-title h-display text-foreground">
        {title}
      </h2>
      {subtitle && (
        <p
          className={[
            "sh-sub mt-5 text-[15px] sm:text-[16px] text-muted-foreground leading-[1.55] max-w-[58ch]",
            isCenter ? "mx-auto" : "",
          ].join(" ")}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
};

export default SectionHeader;
